/**
 * 2D electromagnetic field grid for FDTD simulation.
 * Stores E_x, E_y (electric) and H_z (magnetic) field components
 * for Transverse Electric (TE) mode in 2D.
 *
 * Each field is stored per wavelength channel for spectral accuracy.
 */
export interface FieldGridBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

export class FieldGrid {
  readonly id: number;
  readonly bounds: FieldGridBounds;
  readonly resolution: number;
  readonly cellSize: number;
  readonly wavelengthCount: number;

  // TE mode fields: E_x(x,y,λ), E_y(x,y,λ), H_z(x,y,λ)
  // Flattened: [y * resolution * wavelengthCount + x * wavelengthCount + w]
  E_x: Float32Array;
  E_y: Float32Array;
  H_z: Float32Array;

  // PML absorbing boundary layer conductivity
  private pmlWidth: number = 8;
  private pmlSigma: Float32Array; // damping profile per cell distance from boundary

  intensity: Float32Array; // cached intensity per spatial cell (summed over wavelengths)

  private static nextId = 1;

  constructor(bounds: FieldGridBounds, resolution: number, wavelengthCount: number) {
    this.id = FieldGrid.nextId++;
    this.bounds = bounds;
    this.resolution = resolution;
    this.wavelengthCount = wavelengthCount;
    this.cellSize = bounds.width / resolution;

    const totalCells = resolution * resolution * wavelengthCount;
    this.E_x = new Float32Array(totalCells);
    this.E_y = new Float32Array(totalCells);
    this.H_z = new Float32Array(totalCells);
    this.intensity = new Float32Array(resolution * resolution);

    // Build PML damping profile
    this.pmlSigma = new Float32Array(this.pmlWidth);
    for (let i = 0; i < this.pmlWidth; i++) {
      // Polynomial grading: σ increases quadratically into boundary
      const depth = (this.pmlWidth - i) / this.pmlWidth;
      this.pmlSigma[i] = 0.5 * depth * depth;
    }
  }

  /** Get flat index for field arrays */
  index(x: number, y: number, w: number): number {
    return (y * this.resolution + x) * this.wavelengthCount + w;
  }

  /** Convert world position to grid coordinates */
  worldToGrid(wx: number, wy: number): { gx: number; gy: number } {
    return {
      gx: Math.floor((wx - this.bounds.x) / this.cellSize),
      gy: Math.floor((wy - this.bounds.y) / this.cellSize),
    };
  }

  /** Convert grid coordinates to world position */
  gridToWorld(gx: number, gy: number): { wx: number; wy: number } {
    return {
      wx: this.bounds.x + (gx + 0.5) * this.cellSize,
      wy: this.bounds.y + (gy + 0.5) * this.cellSize,
    };
  }

  /** Get PML damping factor at grid position (0 = no damping, >0 = boundary) */
  getPMLDamping(x: number, y: number): number {
    let sigma = 0;
    if (x < this.pmlWidth) sigma = Math.max(sigma, this.pmlSigma[x]);
    if (y < this.pmlWidth) sigma = Math.max(sigma, this.pmlSigma[y]);
    if (x >= this.resolution - this.pmlWidth) sigma = Math.max(sigma, this.pmlSigma[this.resolution - 1 - x]);
    if (y >= this.resolution - this.pmlWidth) sigma = Math.max(sigma, this.pmlSigma[this.resolution - 1 - y]);
    return sigma;
  }

  /** Compute per-cell intensity from E fields (summed over wavelengths) */
  updateIntensity(): void {
    for (let y = 0; y < this.resolution; y++) {
      for (let x = 0; x < this.resolution; x++) {
        let totalIntensity = 0;
        for (let w = 0; w < this.wavelengthCount; w++) {
          const idx = this.index(x, y, w);
          const ex = this.E_x[idx];
          const ey = this.E_y[idx];
          totalIntensity += ex * ex + ey * ey;
        }
        this.intensity[y * this.resolution + x] = totalIntensity;
      }
    }
  }

  /** Clear all field data */
  clear(): void {
    this.E_x.fill(0);
    this.E_y.fill(0);
    this.H_z.fill(0);
    this.intensity.fill(0);
  }

  /** Memory usage in bytes */
  get memoryBytes(): number {
    return (this.E_x.byteLength + this.E_y.byteLength + this.H_z.byteLength + this.intensity.byteLength);
  }
}

/**
 * Pool for reusing FieldGrid allocations to avoid GC pressure.
 */
export class FieldGridPool {
  private available: FieldGrid[] = [];
  private maxPoolSize: number;

  constructor(maxPoolSize: number = 10) {
    this.maxPoolSize = maxPoolSize;
  }

  acquire(bounds: FieldGridBounds, resolution: number, wavelengthCount: number): FieldGrid {
    // Try to find a compatible grid in the pool
    for (let i = 0; i < this.available.length; i++) {
      const grid = this.available[i];
      if (grid.resolution === resolution && grid.wavelengthCount === wavelengthCount) {
        this.available.splice(i, 1);
        // Update bounds and clear
        (grid as { bounds: FieldGridBounds }).bounds = bounds;
        grid.clear();
        return grid;
      }
    }
    return new FieldGrid(bounds, resolution, wavelengthCount);
  }

  release(grid: FieldGrid): void {
    grid.clear();
    if (this.available.length < this.maxPoolSize) {
      this.available.push(grid);
    }
  }
}
